// ============================================================================
// ELIGIBILITY CRITERIA HOOKS — Phase E
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { eligibilityCriteriaService } from '../../api/services/eligibilityCriteriaService';
import type {
  EligibilityCriteriaListItem,
  EligibilityCriteriaSearchRequest,
  EligibilityCriteriaSet,
  EligibilityField,
} from '../../api/types/eligibilityCriteria.types';

/** Paginated criteria list for the master grid. */
export const useEligibilityCriteriaList = (params?: EligibilityCriteriaSearchRequest) => {
  const [data, setData] = useState<EligibilityCriteriaListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1, pageSize: 25, totalPages: 0, totalItems: 0,
    hasNextPage: false, hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);
  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await eligibilityCriteriaService.getAll(params);
      setData(res.data);
      setPagination(res.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, pagination, refetch: fetch };
};

/** Active field catalog (builder dropdown). Loaded once. */
export const useEligibilityFields = () => {
  const [data, setData] = useState<EligibilityField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await eligibilityCriteriaService.getFields());
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
};

/** Criteria sets for a case type. refreshKey forces a refetch after save. */
export const useEligibilityCriteria = (
  caseTypeCode: string | undefined,
  includeHistory = false,
  refreshKey?: number,
) => {
  const [data, setData] = useState<EligibilityCriteriaSet[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!caseTypeCode) { setData([]); return; }
    setLoading(true);
    setError(null);
    try {
      setData(await eligibilityCriteriaService.getByCaseType(caseTypeCode, includeHistory));
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [caseTypeCode, includeHistory]);

  useEffect(() => { fetch(); }, [fetch, refreshKey]);
  return { data, loading, error, refetch: fetch };
};
